// 📦 Core Libraries
import React, { useEffect, useState, useRef } from 'react';
import { observer } from "mobx-react-lite";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// 🧠 State Management
import pageBuilderStore from "../stores/pageBuilderStore";
import glideBarManager from "../stores/glidebar-manager";
import contentManager from "../stores/content-manager";
import blocksManager from '../stores/blocks-manager';
import bodyStylesManager from "../stores/body-styles-manager";
import serializationManager from "../stores/serialization-manager";
import contentHistoryManager from '../stores/content-history-manager';

// 🗂️ Types & Interfaces
import { ITypoStyles } from '../stores/state-interfaces';
import { IImageBlockData, IHeroSliderBlockData } from '../types/content-block-data';
import { IImageAsset } from './ui/shared/draggable-asset';

// 🧱 Layout Components
import TopBar from './layouts/top-bar';
import SidebarPanel from './layouts/sidebar-panel';

// 🎨 UI Editors & Toolbars
import CodeEditor from "./ui/code-editor";
import DesignCanvas from "./ui/design-canvas";
import EditorToolbar from "./ui/editor-toolbar";
import GlideBar from "./ui/shared/glide-bar";
import PagePreview from './ui/page-preview';
import PreviewToolbar from './ui/preview-toolbar';
import CustomDragPreview from './ui/shared/custom-drag-preview';

// 📷 Image & Media Utilities
import ImageLibraryDialog from './ui/shared/image-library-dialog';
import CarouselBottomBar from './ui/hero-slider-bottom-panel';
import SlideDragPreview from './ui/shared/hero-slider/slide-drag-preview';
import HeroSliderBottomPanel from "./ui/hero-slider-bottom-panel";

/**
 * Props for the main Satori PageBuilder component.
 *
 * @interface IPageBuilderProps
 *
 * @property {boolean} isEmail
 *   If true, enables email template editing mode (enables merge tags, disables web-only features).
 *
 * @property {string} pageTitle
 *   Title of the page or email being edited. Used for display and metadata.
 *
 * @property {string[]} [customFields]
 *   Optional. Array of available custom fields/merge tags (e.g., ["{first-name}", "{email}"]) for personalization in email mode.
 *
 * @property {string} [orgInitials]
 *   Optional. Organization initials, used for branding or display purposes.
 *
 * @property {boolean} [isEditingContent]
 *   Optional. Controls whether the editor is in content-editing mode (vs. preview, etc).
 */
interface IPageBuilderProps {
  isEmail: boolean;
  pageTitle: string;
  customFields?: string[];
  orgInitials?: string;
  isEditingContent?: boolean;
}


/**
 * PageBuilder – Main Visual Builder Component for Pages & Email Templates
 *
 * @component PageBuilder
 * @description
 * Entry point for Satori PageBuilder. Provides the complete visual editing environment—
 * including drag-and-drop block arrangement, rich text editing, live preview, and export.
 * Designed for seamless integration into the Rezerv Business Portal or any React admin.
 *
 * @features
 * - Switch between Design (drag-and-drop), Code, and Preview modes
 * - Supports modular content blocks (text, image, columns, video, etc.)
 * - Built-in image library, color picker, and property panels
 * - Autosaves changes using MobX observable state
 * - Toast notifications for UX feedback (save, publish, errors)
 * - Responsive UI with device width preview controls
 *
 * @structure
 * - TopBar: Title, actions, and main view toggles
 * - PreviewToolbar: Device preview selector (design/preview mode)
 * - DesignCanvas: Main surface for block arrangement
 * - CodeEditor: HTML/CSS/Text source views
 * - SidebarPanel: Block options, global styles, and image library
 * - GlideBar: Rich text floating toolbar
 * - PagePreview: Live rendered output (email/web page)
 *
 * @integration
 * - Accepts props for email mode, custom fields, initial content, etc.
 * - Wraps `pageBuilderStore` (MobX) as the single source of truth for content/state
 * - Calls provided handlers for save/publish (API integration)
 * - Intended for mounting inside client admin or editor dashboard routes
 *
 * @usage
 * Use as a drop-in visual page/email builder:
 *
 * @example
 * <PageBuilder
 *   isEmail={true}
 *   pageTitle="New Promo Email"
 *   customFields={['{first-name}', '{email}']}
 *   orgInitials="SB"
 * />
 *
 * @remarks
 * - Drag-and-drop powered by `react-dnd`
 * - Code editor and live preview sync with MobX state
 * - Extensible: easy to add blocks, panels, or backend features
 *
 * @since 2025-06-08
 * @lastUpdated 2025-07-07
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
export default observer(function PageBuilder({ 
  isEmail = false, 
  pageTitle, 
  customFields = [],
  orgInitials = "d2d",
  isEditingContent = false,
}: IPageBuilderProps) {
  const canvasRef = useRef<HTMLDivElement>(null);

  /**
   * Performs the autosaving function for draft and published content.
   */
  function startAutoSave() {
    setInterval(() => {
      // If our DesignCanvas is empty, we don't perform an autosave.
      if (contentManager.getJsonContent().length > 0) {
        pageBuilderStore.toggleIsAutoSaving();
        setTimeout(() => {
          const cssContentRaw = [
            ...serializationManager.fromStylesToCssContent(bodyStylesManager.getBodyStylesState()),
            ...serializationManager.serializeAllBlockCssContent(contentManager.getDesignContent())
          ];

          contentManager.setCssContent(contentManager.dedupeCssContent(cssContentRaw));
          contentManager.setHtmlContent(serializationManager.fromBlocksToHTML(contentManager.getDesignContent()));
          contentManager.setJsonContent(serializationManager.fromBlocksToJSON(contentManager.getDesignContent()));

          pageBuilderStore.toggleIsAutoSaving();

          pageBuilderStore.setToast(
            pageBuilderStore.isPublished
              ? pageBuilderStore.isEmail ? "📥 Published Email Template" : "📥 Published Page"
              : pageBuilderStore.isEmail ? "💾 Autosaved Email Template" : "💾 Autosaved Draft"
          );
        }, 3000);
      }
    }, 7000);
  }

  useEffect(() => {
    pageBuilderStore.setIsEmail(isEmail);
    pageBuilderStore.setPageTitle(pageTitle);
    pageBuilderStore.setCustomFields(customFields);
    pageBuilderStore.setOrgInitials(orgInitials);
    // Why are these props in the dependency array?
    // Any time the email mode, page title, custom-fields or org initials change, 
    // we want to keep the MobX store in sync.
    // This ensures any changes from the parent component are immediately 
    // reflected in the builder state. 👇
  }, [isEmail, pageTitle, customFields, orgInitials]);

  useEffect(() => {

    const isEditingContent = pageBuilderStore.getIsEditingContent();
    const isEmail = pageBuilderStore.getIsEmail();

    // 1. Set initial state with all core/defaults (safe even if global-css.json is missing)
    bodyStylesManager.setBodyStylesState({
      layout: {
        // ...your DEFAULT_PADDING and any other layout defaults
        top: "32", right: "32", bottom: "32", left: "32", unit: "px", linked: true,
        // plus other layout fields if you have them
      },
      typography: { ...pageBuilderStore.CORE_TYPOGRAPHY_ROLES } as ITypoStyles,
      activeTextRole: "body",
      activeHeadingLevel: "h1",
    });

    // 2. Try seeding with global-css.json (simulates Web API or existing site)
    // In testing our editing mode, we will load the updated-global-css.json to simulate a 
    // page that has been previously saved and is being loaded for editing.
    fetch(isEditingContent ? isEmail ? "/data-api/updated-email-css.json" : "/data-api/updated-global-css.json" : "/data-api/global-css.json")
      .then(res => res.json())
      .then((cssContent) => {
        // Store globally (MobX, React state, etc)
        contentManager.setCssContent([]);
        contentManager.setCssContent(cssContent);

        // Merge/override into the initialized state
        const seeded = serializationManager.fromCSSContentToBodyStyles(
          cssContent,
          {
            layout: {
              containerType: "boxed",
              maxWidth: { value: "1200", unit: "px" },
              padding: { top: "32", right: "32", bottom: "32", left: "32", unit: "px", linked: true },
              margin: { top: "0", right: "0", bottom: "0", left: "0", unit: "px", linked: true },
              backgroundColor: "#ffffff",
              // width not needed, handled in serializer
            },
            typography: { ...pageBuilderStore.CORE_TYPOGRAPHY_ROLES } as ITypoStyles,
            activeTextRole: "p",
            activeHeadingLevel: "h1",
          }
        );
        bodyStylesManager.setBodyStylesState(seeded);

        // If isEditingContent is true, we will also deserialize the content blocks and seed them into our 
        // contentManager.setDesignContent(). But we must first call the serialization method 
        // `fromJSONToBlocks(jsonArray: any): IDesignBlock[]`
        if (pageBuilderStore.getIsEditingContent()) {
          // We will do a fetch and use `/data-api/page-data.json`
          fetch(isEmail ? "/data-api/email-data.json" : "/data-api/page-data.json").then(res => res.json()).then(jsonArray => {
            const blocks = serializationManager.fromJSONToBlocks(jsonArray);
            contentManager.setDesignContent(blocks);
                        
            // We don't have to recreate the designTree here. We can perform it just before we render our content 
            // blocks onto the canvas.
          });
        }
      })
      .catch(() => {
        // If fetch fails (no file, etc), do nothing—defaults are already loaded.
        // Optionally, show a toast/console.log for missing file
      });

  }, [pageBuilderStore.getIsEditingContent()]);

  useEffect(() => {
    fetch("/data-api/image-gallery.json").then(res => res.json()).then(assets => pageBuilderStore.setImageLibraryAssets(assets));
  }, []);

  useEffect(() => {
    startAutoSave();
  }, []);

  return (
    <DndProvider debugMode={true} backend={HTML5Backend}>
      <div className="pagebuilder-container">
        {/* Top Bar (two rows) */}
        <TopBar />

        {pageBuilderStore.previewMode.showPreview ? (
          <>
            {/* Page Preview mode  */}
            <PagePreview />
          </>
        ):(
          <>
            {/* Design/Editing mode  */}
            <div 
              className="flex flex-1 border-r border-l border-t border-gray-300 relative z-5"
            >
              <ImageLibraryDialog 
                open={blocksManager.getOpenGalleryDialog()}
                images={pageBuilderStore.getImageLibraryAssets()}
                onClose={() => blocksManager.setOpenGalleryDialog(false)}
                onSelect={(img) => {
                  const editing = blocksManager.getBlockEditing();
                  const block = contentManager.getContentBlockById(editing.selectedBlockId!);
                  if (!block) return;

                  if (block.blockType === "image") {
                    // Image Block: update its image
                    const imageData = block.blockData as IImageBlockData;
                    contentManager.setImageConfig(block.contentBlockId, {
                      ...imageData,
                      src: img.url,
                    });
                  } else if (block.blockType === "hero_slider") {
                    // HeroSlider: update the selected slide's image
                    const heroSliderData = block.blockData as IHeroSliderBlockData;
                    const selectedIndex = blocksManager.getHeroSlideEditing().selectedSlideIndex ?? 0;
                    const updatedSlides = heroSliderData.slides.map((slide, i) =>
                      i === selectedIndex
                        ? {
                            ...slide,
                            slideImage: {
                              ...slide.slideImage,
                              src: img.url,
                            },
                          }
                        : slide
                    );
                    contentManager.setHeroSliderConfig(block.contentBlockId, { slides: updatedSlides });
                  }
                }}
              />
              
              {/* Main Editor Area */}
              {pageBuilderStore.selectedTab === "Design" && (
                <div className="flex flex-col h-[801px] w-[1200px] "
                  style={{
                    maxWidth: "1200"
                  }}
                  onClick={()=> {
                    glideBarManager.hideGlideBar();
                    glideBarManager.setIsEditingHeading(false);
                    glideBarManager.setIsEditingText(false);
                  }}
                >
                  <div className="flex-1 bg-white flex items-center justify-center">
                    <div ref={canvasRef} className="relative flex flex-col w-full items-center h-[802px] justify-center bg-gray-50">
                      <PreviewToolbar />
                        
                      <DesignCanvas />
                      <CustomDragPreview />
                        
                      <SlideDragPreview slide={blocksManager.getHeroSlideEditing().selectedSlide!} />

                      {/* GlideBar floats above active block (if any) */}
                      {pageBuilderStore.glideBarState.isVisible &&
                        pageBuilderStore.glideBarState.contentBlockRef && (
                        <GlideBar
                            isVisible={pageBuilderStore.glideBarState.isVisible}
                            contentType={pageBuilderStore.glideBarState.blockType! !== "heading" ? "text" : "heading"}
                            targetRef={pageBuilderStore.glideBarState.contentBlockRef}
                            canvasRef={canvasRef}
                            // ...other props
                          />
                        )}
                      
                      {/* Our CarouselBottomBar */}
                      {blocksManager.getBlockEditing().selectedBlockType === "hero_slider" && (
                        <HeroSliderBottomPanel
                          blockId={blocksManager.getBlockEditing().selectedBlockId!}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
              {(pageBuilderStore.selectedTab === "HTML" || pageBuilderStore.selectedTab === "Styles" || pageBuilderStore.selectedTab === "Text") && (
                <div 
                  className="flex flex-col w-full h-full items-center justify-center"
                  style={{
                    width: "1600px"
                  }}
                >
                  <CodeEditor editorMode={pageBuilderStore.selectedTab} />
                </div>
              )}
              {pageBuilderStore.selectedTab === "Design" && (
                <div className="flex flex-col h-[801px] bg-white z-50 w-[350px] overflow-y-auto"
                  style={{
                    borderLeft: "1px",
                    borderStyle: "solid"
                  }}
                >
                  <SidebarPanel />
                </div>
              )}

              {/* Right Toolbar */}
              {pageBuilderStore.selectedTab === "Design" && <EditorToolbar />}
              {/*
                TODO: Right now, this only applies to a small narrow area below the toolbar buttons. I need the
                      trigger to be anywhere outside of the panel (anywhere the user clicks on the page)
                Overlay to close panel when clicking outside
              */}
              {pageBuilderStore.activePanel && (
                <div
                  className="fixed inset-0 z-0"
                  onClick={() => {
                    blocksManager.setBlockEditing(undefined, undefined);
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
      {pageBuilderStore.toastMessage && (
        <div className="fixed bottom-4 left-4 bg-gray-900 text-white text-sm px-4 py-2 rounded shadow z-50">
          {pageBuilderStore.toastMessage}
        </div>
      )}
    </DndProvider>
  );
});
